import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChange, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { TranslateService } from '@app/core/services/translate.service';
import { concatMap, debounceTime, distinctUntilChanged, from, of, Subject, Subscription, takeUntil } from 'rxjs';
import { RememberService } from '@app/core/services/remember.service';
import { AppConfigService } from '@app/services';

/**
 * Represents a card/well. Content is passed through the element.
 */
@Component({
    selector: 'tt-card',
    templateUrl: './card.component.html',
    styleUrls: ['./card.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class CardComponent implements OnInit, OnChanges, OnDestroy {
    /**
     *  (Optional) a heading for the card.
     */
    @Input()
    public ttHeading?: string;

    /**
     * The headed appended to the template.
     */
    public heading = '';

    /**
     * Whether the heading for the card should be translated or not.
     *
     * @default true
     */
    @Input()
    public set ttTranslateHeading(value: BooleanInput) {
        this._translateHeading = coerceBooleanProperty(value);
    }
    public get ttTranslateHeading(): boolean {
        return this._translateHeading;
    }
    private _translateHeading = true;

    /**
     * Whether the card is an accordion as well.
     *
     * @default false
     */
    @Input()
    public set ttAccordion(value: BooleanInput) {
        this._accordion = coerceBooleanProperty(value);
    }
    public get ttAccordion(): boolean {
        return this._accordion;
    }
    private _accordion = false;

    /**
     * Used to override the current accordion state. if null the internal accordion state is used, if true or false it will override the current state.
     */
    @Input()
    public ttOpen: boolean | null = null;

    public accordionOpen = true;

    public get isAccordionOpen() {
        return (this.accordionOpen && this.ttOpen === null) || this.ttOpen === true;
    }

    public get isAccordionClosed() {
        return (!this.accordionOpen && this.ttOpen === null) || this.ttOpen === false;
    }

    public accordionOpen$ = new Subject<boolean>();

    @Input()
    public ttIcon?: string;

    @Input()
    public ttClass?: string;

    /**
     * The id to save the state of the card with, only applicable if accordion.
     */
    @Input()
    public ttRememberId: string = '';

    /**
     * Milliseconds between the storing of each change if ttRememberId is provided.
     *
     * @default 500
     */
    @Input()
    public set ttRememberDebounceTime(value: NumberInput) {
        this._rememberDebounceTime = coerceNumberProperty(value);
    }
    public get ttRememberDebounceTime(): number {
        return this._rememberDebounceTime;
    }
    private _rememberDebounceTime: number = 500;

    /**
     * Whether to only persist the state locally (in localstorage) or to store it in 616 if false.
     *
     * @default true
     */
    @Input()
    public set ttPersistLocalOnly(value: BooleanInput) {
        this._persistLocalOnly = coerceBooleanProperty(value);
    }
    public get ttPersistLocalOnly(): boolean {
        return this._persistLocalOnly;
    }
    private _persistLocalOnly = true;

    constructor(
        private appConfig: AppConfigService,
        private translate: TranslateService,
        private rememberService: RememberService,
    ) {}

    public toggleAccordion() {
        this.accordionOpen = !this.accordionOpen;
        this.accordionOpen$.next(this.accordionOpen);
    }

    private remember(state: boolean) {
        const storedCards = this.getLocalStorageItem();
        storedCards[this.ttRememberId] = state;

        localStorage.setItem(this.getLocalStorageName(), JSON.stringify(storedCards));

        if (this.ttPersistLocalOnly) {
            return of();
        } else {
            return from(this.rememberService.remember(this.ttRememberId, state));
        }
    }

    private rememberSubscription?: Subscription;

    private destroy$ = new Subject<void>();

    private async getLastState() {
        if (!this.ttAccordion || !this.ttRememberId) return;

        this.accordionOpen = this.getLocalStorageItem()[this.ttRememberId] ?? true;

        if (!this.ttPersistLocalOnly) {
            const last = (await this.rememberService.getLastStatus(this.ttRememberId))[0].variablevalue;
            const lastStatus = ![0, '0', 'false', false].includes(last);
            this.accordionOpen = lastStatus;
        }
    }

    private getLocalStorageName(): string {
        return `tt-card.${this.appConfig.settings.user!.portalUserKeynoEdit}`;
    }

    private getLocalStorageItem(): { [key: string]: boolean } {
        try {
            const name = this.getLocalStorageName();
            return JSON.parse(localStorage.getItem(name) ?? '{}');
        } catch (error) {
            console.error(error);
            return {};
        }
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['ttAccordion']) {
            this.ttAccordion = changes['ttAccordion'].currentValue;
            this.getLastState();
        }

        if (changes['ttTranslateHeading']) {
            this.ttTranslateHeading = changes['ttTranslateHeading'].currentValue;
        }

        if (changes['ttPersistLocalOnly']) {
            this.ttPersistLocalOnly = changes['ttPersistLocalOnly'].currentValue;
        }

        if (changes['ttRememberDebounceTime']) {
            this.ttRememberDebounceTime = changes['ttRememberDebounceTime'].currentValue;
        }

        if (changes['ttHeading']) {
            this.setHeading(changes['ttHeading']);
        }

        if (!!changes['ttRememberId'] || !!changes['ttRememberDebounceTime']) {
            this.setupRemember();
        }
    }

    private async setHeading(heading: SimpleChange) {
        if (!heading.currentValue) {
            this.heading = '';
        } else if (typeof heading.currentValue === 'string' && (heading.currentValue !== heading.previousValue || heading.firstChange)) {
            if (this.ttTranslateHeading) {
                this.heading = await this.translate.translate(heading.currentValue);
            } else {
                this.heading = heading.currentValue;
            }
        }
    }

    private setupRemember() {
        if (!!this.rememberSubscription) {
            this.rememberSubscription.unsubscribe();
            delete this.rememberSubscription;
        }

        this.rememberSubscription = this.accordionOpen$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(this.ttRememberDebounceTime),
                distinctUntilChanged(),
                concatMap((open) => this.remember(open)),
            )
            .subscribe();
    }

    ngOnInit(): void {
        // this.getLastState();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
